/* eslint-disable react-hooks/exhaustive-deps */
import { appConfig } from '@common/config';
import useClickAway from '@hook/useClickAway';
import { type IServer, type IServerList } from '@interface/ICommon';
import { ServerListQueryKey, WorldServerListQueryKey } from '@network/queryKeys';
import { useDataQuery } from '@network/querys';
import classNames from 'classnames';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PURPLEZONE_SERVER_ID_KEY } from '@constants/common';

export const ServerDropdown = ({ props }: { props: IServerDDProp }) => {
    const {
        submitCallback, defaultSvrId, changeSvrId,
        tradableWorld, useAllSvrBtn, useWorldSvr,
        autoAllWhenLoggedOut = true,
        hideEmptyWorlds = false,
    } = props;

    const { t } = useTranslation();
    const { data: serverList } = useDataQuery<IServerList[]>(
        ServerListQueryKey,
        `/${appConfig.service}/v2.0/game/server`,
        {},
        {
            staleTime: Infinity,
        }
    );
    const { data: serverListW } = useDataQuery<IServer[]>(
        WorldServerListQueryKey,
        `/${appConfig.service}/v2.0/game/market/world`,
        {},
        {
            staleTime: Infinity,
            enabled: useWorldSvr,
        }
    );

    const emitScope = (detail: { serverId?: string; worldId?: string }) => {
        window.dispatchEvent(new CustomEvent('scope/change', { detail }));
    };

    
    // 로그인 상태에 따른 초기값 설정
    const getInitialState = () => {
        if (window.userData?.isLogin === false) {
            return { d1: 0, d2: 0, title: t('noti.allservers') };
        }
        return {
            d1: 0,
            d2: Number(props.defaultSvrId),
            title: props.defaultSvrId ? '' : t('noti.allservers')
        };
    };


    const [isOpen, setIsOpen] = useState(false);
    const [svrList, svrListSet] = useState<IServerList[]>([]);
    const [svrList2d, svrList2dSet] = useState<IServer[]>([]);
    const [nowSelect, nowSelectSet] = useState(getInitialState);
    const [initialized, setInitialized] = useState(false);

    // 로그인 상태 변경 감지
    useEffect(() => {
        if (!initialized) return;
        
        if (window.userData?.isLogin === false) {
            if (props.forceFirstServerWhenLoggedOut) {
                const firstWorld = svrList[0];
                if (firstWorld) {
                    window.dispatchEvent(
                        new CustomEvent('scope/change', { 
                            detail: { worldId: String(firstWorld.worldId) } 
                        })
                    );
                    selectComplete(
                        String(firstWorld.worldId),
                        [], // 빈 배열로 설정하여 서버 목록 비우기
                        {
                            d1: firstWorld.worldId,
                            d2: 0,
                            title: firstWorld.worldName
                        },
                        true
                    );
                }
            } else if (props.autoAllWhenLoggedOut) {
                const first = svrList?.[0];
                const list = first?.servers ?? [];
                selectComplete('all', list, { d1: 0, d2: 0, title: t('noti.allservers') }, true);
                emitScope({ serverId: 'all' });
            }
        }
    }, [window.userData?.isLogin, initialized, svrList]);

    // ⬇️ 그 다음에 계산 (TDZ 회피)
    const visibleWorlds = useMemo(
        () =>
            hideEmptyWorlds
                ? svrList.filter(w => w.worldId === 0 || (w.servers?.length ?? 0) > 0) // worldId 0은 항상 표시
                : svrList,
        [svrList, hideEmptyWorlds]
    );
    useEffect(() => {
        if (!serverList || serverList.length < 1) return;

        const base = serverList.map(item => ({
            ...item,
            worldId: item.worldId || getRandomInt(50000, 99999),
        }));

        let servers = base;
        if (useWorldSvr && Array.isArray(serverListW)) {
            servers = [
                { worldName: t('price.worldexchange'), worldId: 0, servers: serverListW },
                ...base,
            ];
        }

        svrListSet(servers);

        // 최초 1회만 초기 설정 실행
        if (!initialized) {
            settting(servers);
            setInitialized(true);
        }
    }, [serverList, serverListW, useWorldSvr, initialized]);

    // changeSvrId에 대한 처리는 별도로 분리
    useEffect(() => {
        if (svrList.length < 1 || changeSvrId === null || !initialized) return;

        if (changeSvrId === 'all') {
            set2d(svrList[0]?.servers ?? []);
            nowSelectSet({ d1: 0, d2: 0, title: t('noti.allservers') });
            return;
        }

        const sid = Number(changeSvrId);
        if (Number.isNaN(sid)) return;

        // 1) 먼저 서버ID로 매칭 시도
        const serverWorldMeta = findWorldIdByServerId(sid, svrList);
        if (serverWorldMeta) {
            const world = findObjectByWorldId(svrList, serverWorldMeta.id);
            if (world) {
                if (appConfig.service === 'l2m') {
                    // l2m: 월드만 표시
                    selectComplete(
                        String(world.worldId),
                        [],
                        { d1: world.worldId, d2: 0, title: world.worldName }
                    );
                } else {
                    // 다른 서비스: 기존 처리 유지
                    const target = world?.servers?.find(s => s.serverId === sid);
                    if (target) {
                        selectComplete(
                            String(sid),
                            world.servers,
                            { d1: world.worldId, d2: sid, title: `${world.worldName} : ${target.serverName}` }
                        );
                    }
                }
                return;
            }
        }

        // 2) 서버 매칭 실패 → 월드ID로 폴백
        const worldById = findObjectByWorldId(svrList, sid);
        if (worldById) {
            // 월드만 선택(d2:0), 2뎁스 리스트는 worldById.servers(여기선 빈 배열)
            selectComplete(
                String(worldById.worldId),
                worldById.servers ?? [],
                { d1: worldById.worldId, d2: 0, title: worldById.worldName }
            );
            return;
        }

        // 3) 그래도 못 찾으면 아무 것도 하지 않음
    }, [svrList, initialized]);

    const isValidServer = (s: any): s is IServer =>
        !!s && typeof s.serverId === 'number' && typeof s.serverName === 'string';

    const set2d = (list?: IServer[] | null) => {
        svrList2dSet(Array.isArray(list) ? list.filter(isValidServer) : []);
    };

    // ✅ 서버명 끝 숫자 제거(카인06 -> 카인, 사이하10 -> 사이하, 에덴003 -> 에덴)
    const getWorldNameFromServerName = (name?: string) =>
        (name ?? '').replace(/\s*\d+$/, '').trim();

// ✅ 비교 안정화용(공백 제거 + NFC 정규화)
    const normalize = (s?: string) => (s ?? '').normalize('NFC').replace(/\s+/g, '');

    // settting 함수 수정
    const settting = (servers: IServerList[]) => {
        // 로그아웃 상태 먼저 체크
        if (window.userData?.isLogin === false) {
            if (props.forceFirstServerWhenLoggedOut) {
                const firstWorld = servers[0];
                if (firstWorld) {
                    window.dispatchEvent(
                        new CustomEvent('scope/change', { 
                            detail: { worldId: String(firstWorld.worldId) } 
                        })
                    );
                    selectComplete(
                        String(firstWorld.worldId),
                        [], 
                        {
                            d1: firstWorld.worldId,
                            d2: 0,
                            title: firstWorld.worldName
                        },
                        true
                    );
                }
                return;
            } else if (props.autoAllWhenLoggedOut) {
                const first = servers?.[0];
                const list = first?.servers ?? [];
                selectComplete('all', list, { d1: 0, d2: 0, title: t('noti.allservers') }, true);
                emitScope({ serverId: 'all' });
                return;
            }
            return;
        }

        const preferredSid = Number(defaultSvrId);
        if (!Number.isNaN(preferredSid) && preferredSid > 0) {
            const worldMeta = findWorldIdByServerId(preferredSid, servers);
            if (worldMeta) {
                const world = findObjectByWorldId(servers, worldMeta.id);
                if (world) {
                    if (appConfig.service === 'l2m') {
                        // l2m인 경우: 월드 이름만 표시
                        window.dispatchEvent(new CustomEvent('scope/change', { detail: { worldId: String(world.worldId) } }));
                        selectComplete(
                            String(world.worldId),
                            [], // 빈 배열로 설정하여 서버 목록 비우기
                            { d1: world.worldId, d2: 0, title: world.worldName }, // title에 월드 이름만 설정
                            true
                        );
                    } else {
                        // l2m이 아닌 경우: 기존 로직 유지
                        const target = world?.servers?.find((s) => s.serverId === preferredSid);
                        if (target) {
                            window.dispatchEvent(new CustomEvent('scope/change', { detail: { serverId: String(preferredSid) } }));
                            selectComplete(
                                String(preferredSid),
                                world.servers,
                                { d1: world.worldId, d2: preferredSid, title: `${world.worldName} : ${target.serverName}` },
                                true
                            );
                        }
                    }
                    return;
                }
            }
        }

        if (appConfig.service === 'l2m') {
            // changeSvrId가 서버ID로 들어와도 월드로 승격 선택
            if (typeof changeSvrId === 'string' && /^\d+$/.test(changeSvrId)) {
                const sid = Number(changeSvrId);
                const worldMeta = findWorldIdByServerId(sid, servers);
                if (worldMeta) {
                    const world = findObjectByWorldId(servers, worldMeta.id);
                    if (world) {
                        // ✅ 월드만 선택: d2=0, title=world.worldName, 2depth 비우기
                        set2d([]);
                        window.dispatchEvent(new CustomEvent('scope/change', { detail: { worldId: String(world.worldId) } }));
                        selectComplete(
                            String(world.worldId),
                            [], // 빈 배열 넘겨 worldId emit 유도
                            { d1: world.worldId, d2: 0, title: world.worldName },
                            true
                        );
                        return;
                    }
                }
            }

            // 계정 serverId가 있어도 월드만 선택
            const accountSid = Number(window.userData?.gameAccount?.serverId);
            if (!Number.isNaN(accountSid) && accountSid > 0) {
                const worldMeta = findWorldIdByServerId(accountSid, servers);
                if (worldMeta) {
                    const world = findObjectByWorldId(servers, worldMeta.id);
                    if (world) {
                        set2d([]);
                        window.dispatchEvent(new CustomEvent('scope/change', { detail: { worldId: String(world.worldId) } }));
                        selectComplete(
                            String(world.worldId),
                            [],
                            { d1: world.worldId, d2: 0, title: world.worldName },
                            true
                        );
                        return;
                    }
                }
            }
        }else{
            // 3-1) 계정의 serverId가 있으면 ID로 직접 매칭
            const accountSid = Number(window.userData?.gameAccount?.serverId);
            if (!Number.isNaN(accountSid) && accountSid > 0) {
                const worldMeta = findWorldIdByServerId(accountSid, servers);
                if (worldMeta) {
                    const world = findObjectByWorldId(servers, worldMeta.id);
                    const target = world?.servers?.find((s) => s.serverId === accountSid);
                    if (world && target) {
                        window.dispatchEvent(new CustomEvent('scope/change', { detail: { serverId: String(accountSid) } }));
                        selectComplete(
                            String(accountSid),
                            world.servers,
                            { d1: world.worldId, d2: accountSid, title: `${world.worldName} : ${target.serverName}` },
                            true
                        );
                        return;
                    }
                }
            }
        }

        // 4) 그 외엔 자동 선택하지 않음 (비선택 상태 유지)
        set2d([]);
        nowSelectSet({ d1: 0, d2: 0, title: '' });
        // scope/change emit 안 함 (유저가 직접 선택)
    };

    const d1Click = (_wid: number) => {
        const list = findObjectByWorldId(svrList, _wid);
        if (!list) return;

        if (appConfig.service === 'l2m') {
            // ✅ 월드만 선택
            set2d([]);
            selectComplete(String(_wid), [], { d1: _wid, d2: 0, title: list.worldName });
            return;
        }

        // 기존 동작 (l2m 외)
        set2d(list.servers);
        nowSelectSet((cur) => ({ ...cur, d1: _wid }));

        if (!list.servers || list.servers.length === 0) {
            selectComplete(String(_wid), [], { d1: _wid, d2: 0, title: list.worldName });
            return;
        }
    };

    const d2Click = (_sid: number, _sname: string) => {
        const dat = findWorldIdByServerId(_sid, svrList);
        if (!dat) return;
        selectComplete(String(_sid), undefined, {
            d1: dat.id,
            d2: _sid,
            title: `${dat.name} : ${_sname}`,
        });
    };

    const firstWithServers = () =>
        (hideEmptyWorlds ? svrList.find(w => (w.servers?.length ?? 0) > 0) : svrList[0]) ?? { servers: [] as IServer[] };

    const allClick = () => {
        const first = firstWithServers();
        const list = first.servers ?? [];
        selectComplete('all', list, { d1: 0, d2: 0, title: t('noti.allservers') });
    };

    const selectComplete = (
        _d: string,
        svr?: IServer[],
        selObj?: { d1?: number; d2?: number; title: string; },
        isFirstSetting: boolean = false
    ) => {
        setIsOpen(false);
        if (svr) set2d(svr);
        if (selObj) nowSelectSet((current) => ({ ...current, ...selObj }));

        // ✅ 여기서 항상 scope emit
        if (_d === 'all') {
            emitScope({ serverId: 'all' });
        } else if (selObj?.d2 && selObj.d2 > 0) {
            emitScope({ serverId: String(selObj.d2) });
        } else if (selObj?.d1 && (!svr || svr.length === 0)) {
            // 서버 없는 월드(1뎁스에서 끝)인 경우
            emitScope({ worldId: String(selObj.d1) });
        }

        submitCallback(_d, isFirstSetting);
    };

    const wrapRef = useRef<HTMLDivElement>(null);
    useClickAway(wrapRef, (e) => {
        setIsOpen(false);
    });


    return (
        <div className="ui-dropdown-wrap dropdown-2depth">
            <div className={classNames({ 'is-active': isOpen }, 'ui-dropdown ui-dropdown-community')}>
                <div className="select" onClick={() => setIsOpen(!isOpen)}>
                    <span className="selectedCategory" data-areas="__서버_클릭">
                        {nowSelect.title}
                    </span>
                </div>
                <div
                     className={classNames(
                        'option option-2depth',
                        { 'no-2depth': appConfig.service === 'l2m' }
                    )}
                     ref={wrapRef}
                >
                    {useAllSvrBtn && (
                        <div className="option-menu option-menu-all" onClick={allClick}>
                            {t('noti.allservers')}
                        </div>
                    )}
                    <div className={classNames(
                        'option-menu option-menu-1depth',
                        { 'no-2depth': appConfig.service === 'l2m' }
                    )}>
                        <ul className="option-list">
                            {visibleWorlds.map((svr, idx) => (
                                <li
                                    key={svr.worldId + '_' + idx}
                                    className={classNames({ 'is-active': nowSelect.d1 === svr.worldId }, 'depth1')}
                                    onClick={() => d1Click(svr.worldId)}
                                >
                                    {svr.worldName}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {appConfig.service !== 'l2m' && (
                        <div
                            className={classNames(
                                'option-menu option-menu-2depth',
                                { 'no-2depth': svrList2d.length === 0 }
                            )}
                        >
                            <ul className="option-list menu2depthList">
                                {svrList2d.filter(Boolean).map((svr, idx) => (
                                    <li
                                        key={svr.serverId + '_' + idx}
                                        className={classNames(
                                            { 'is-active': nowSelect.d2 === svr.serverId },
                                            'depth2'
                                        )}
                                        onClick={() => d2Click(svr.serverId, svr.serverName)}
                                    >
                                        {svr.serverName}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export interface IServerDDProp {
    submitCallback: (_d: string, _isFirstSetting?: boolean) => void;
    defaultSvrId: string | undefined;
    changeSvrId: string | null;
    tradableWorld: boolean | undefined;
    useAllSvrBtn: boolean;
    useWorldSvr: boolean;
    autoAllWhenLoggedOut?: boolean;
    forceFirstServerWhenLoggedOut?: boolean;
    hideEmptyWorlds?: boolean;
}

const getRandomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

const findObjectByWorldId = (svrlist: IServerList[], worldId: number): IServerList | undefined => {
    return svrlist.find((obj) => obj.worldId === worldId);
};

const findWorldIdByServerId = (serverId: number, svrlist: IServerList[]): { id: number; name: string } | null => {
    const foundItem = svrlist.find((item) =>
         (item.servers ?? []).some((server) => server && server.serverId === serverId)
    );
    return foundItem ? { id: foundItem.worldId, name: foundItem.worldName } : null;
};

const findServerNameById = (serverId: number, svrlist: IServer[]): string | undefined => {
    const foundServer = (svrlist ?? []).find((server) => server && server.serverId === serverId);
    return foundServer?.serverName;
};